import * as React from "react";
import { ExportReportPdfButton } from "@/components/ExportReportPdfButton";
import { useFinance } from "@/context/FinanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, ChevronRight, FileText, Download, FileSpreadsheet, Columns, Wallet, Landmark } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachMonthOfInterval } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { useAuth } from "@/context/AuthContext";
import { useHierarchyExport } from "@/hooks/useHierarchyExport";
import { DateInput } from "@/components/ui/date-input";

const currency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

interface Level1Data {
  id: string;
  code: string;
  name: string;
}

interface Level2Data {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: "receita" | "despesa";
}

interface Level3Data {
  id: string;
  level2Id?: string;
  code?: string;
  name: string;
  type: "receita" | "despesa";
}

interface TransactionData {
  id: string;
  description: string;
  value: number;
  type: string;
  issueDate?: string;
  paymentDate?: string;
  categoryId: string;
  settled?: boolean;
  supplierId?: string;
  costCenterId?: string;
  accountId?: string;
}

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function FluxoCaixa() {
  const { user } = useAuth();
  const { transactions, suppliers, costCenters, accounts } = useFinance();
  const supabaseSync = useSupabaseSync();

  const [level1Accounts, setLevel1Accounts] = React.useState<Level1Data[]>([]);
  const [level2Accounts, setLevel2Accounts] = React.useState<Level2Data[]>([]);
  const [level3Accounts, setLevel3Accounts] = React.useState<Level3Data[]>([]);

  const currentDate = new Date();
  const [startDate, setStartDate] = React.useState<string>(formatDateForInput(startOfMonth(currentDate)));
  const [endDate, setEndDate] = React.useState<string>(formatDateForInput(endOfMonth(currentDate)));
  const [selectedCostCenter, setSelectedCostCenter] = React.useState<string>("all");

  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({});
  const [isCentered, setIsCentered] = React.useState(true);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const { exportToExcel, exportToPDF } = useHierarchyExport();

  React.useEffect(() => {
    document.title = "DFC - Demonstração do Fluxo de Caixa | Controle Financeiro";
  }, []);

  React.useEffect(() => {
    if (user) loadHierarchyData();
  }, [user]);

  const loadHierarchyData = async () => {
    const [l1Data, l2Data, l3Data] = await Promise.all([
      supabaseSync.fetchChartOfAccountsLevel1(),
      supabaseSync.fetchChartOfAccountsLevel2(),
      supabaseSync.fetchChartOfAccounts(),
    ]);
    setLevel1Accounts(l1Data.map(d => ({ id: d.id, code: d.code, name: d.name })));
    setLevel2Accounts(l2Data.map(d => ({ id: d.id, level1Id: d.level1_id, code: d.code, name: d.name, type: d.type as "receita" | "despesa" })));
    setLevel3Accounts(l3Data.filter(d => !d.hidden).map(d => ({
      id: d.id, level2Id: d.level2_id, code: d.code, name: d.name, type: d.type as "receita" | "despesa",
    })));
  };

  const getSupplierName = (supplierId?: string) => {
    if (!supplierId) return "-";
    return suppliers.find(s => s.id === supplierId)?.name || "-";
  };

  // Period columns
  const periodColumns = React.useMemo(() => {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    if (start > end) return [];
    return eachMonthOfInterval({ start, end }).map(month => ({
      date: month,
      label: format(month, "MMM/yy", { locale: ptBR }).replace(/^./, c => c.toUpperCase()),
      key: format(month, "yyyy-MM"),
    }));
  }, [startDate, endDate]);

  // Filter transactions by paymentDate
  const transactionsByMonth = React.useMemo(() => {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    end.setHours(23, 59, 59, 999);

    const filtered = transactions.filter(t => {
      if (t.kind === "transferencia") return false;
      if (selectedCostCenter !== "all" && t.costCenterId !== selectedCostCenter) return false;
      const dateField = t.paymentDate;
      if (!dateField) return false;
      const txDate = parseLocalDate(dateField);
      return txDate >= start && txDate <= end;
    });

    const byMonth: Record<string, TransactionData[]> = {};
    filtered.forEach(t => {
      if (!t.paymentDate) return;
      const monthKey = format(parseLocalDate(t.paymentDate), "yyyy-MM");
      if (!byMonth[monthKey]) byMonth[monthKey] = [];
      byMonth[monthKey].push(t);
    });

    return { filtered, byMonth };
  }, [transactions, startDate, endDate, selectedCostCenter]);

  // Opening balance per account per month
  const accountBalancesByMonth = React.useMemo(() => {
    const result: Record<string, Record<string, number>> = {};
    // For each account, for each month, calculate: initialBalance + sum of settled txs with paymentDate < month start
    accounts.forEach(account => {
      result[account.id] = {};
      periodColumns.forEach(period => {
        const monthStart = period.date;
        let balance = account.initialBalance || 0;
        transactions.forEach(t => {
          if (t.kind === "transferencia") return;
          if (t.accountId !== account.id) return;
          if (!t.settled) return;
          if (selectedCostCenter !== "all" && t.costCenterId !== selectedCostCenter) return;
          const dateField = t.paymentDate;
          if (!dateField) return;
          const txDate = parseLocalDate(dateField);
          if (txDate < monthStart) {
            balance += t.type === "receita" ? t.value : -t.value;
          }
        });
        result[account.id][period.key] = balance;
      });
    });
    return result;
  }, [accounts, transactions, periodColumns, selectedCostCenter]);

  // Total opening balance per month
  const totalOpeningByMonth = React.useMemo(() => {
    const totals: Record<string, number> = {};
    periodColumns.forEach(period => {
      totals[period.key] = accounts.reduce((sum, acc) => sum + (accountBalancesByMonth[acc.id]?.[period.key] || 0), 0);
    });
    return totals;
  }, [accountBalancesByMonth, periodColumns, accounts]);

  // Build hierarchy data (same as DRE but using paymentDate)
  const hierarchyData = React.useMemo(() => {
    const { byMonth } = transactionsByMonth;

    const level3TotalsByMonth: Record<string, Record<string, { value: number; transactions: TransactionData[] }>> = {};
    const level2TotalsByMonth: Record<string, Record<string, number>> = {};
    const level1TotalsByMonth: Record<string, Record<string, number>> = {};

    level3Accounts.forEach(l3 => {
      level3TotalsByMonth[l3.id] = {};
      periodColumns.forEach(period => {
        const monthTxs = byMonth[period.key] || [];
        const catTxs = monthTxs.filter(t => t.categoryId === l3.id);
        const value = catTxs.reduce((sum, t) => sum + (t.type === "receita" ? t.value : -t.value), 0);
        level3TotalsByMonth[l3.id][period.key] = { value, transactions: catTxs };
      });
    });

    level2Accounts.forEach(l2 => {
      level2TotalsByMonth[l2.id] = {};
      const childL3 = level3Accounts.filter(l3 => l3.level2Id === l2.id);
      periodColumns.forEach(period => {
        level2TotalsByMonth[l2.id][period.key] = childL3.reduce((sum, l3) => sum + (level3TotalsByMonth[l3.id]?.[period.key]?.value || 0), 0);
      });
    });

    level1Accounts.forEach(l1 => {
      level1TotalsByMonth[l1.id] = {};
      const childL2 = level2Accounts.filter(l2 => l2.level1Id === l1.id);
      periodColumns.forEach(period => {
        level1TotalsByMonth[l1.id][period.key] = childL2.reduce((sum, l2) => sum + (level2TotalsByMonth[l2.id]?.[period.key] || 0), 0);
      });
    });

    const grandTotalsByMonth: Record<string, number> = {};
    periodColumns.forEach(period => {
      grandTotalsByMonth[period.key] = level1Accounts.reduce((sum, l1) => sum + (level1TotalsByMonth[l1.id]?.[period.key] || 0), 0);
    });

    const grandTotal = {
      receita: transactionsByMonth.filtered.filter(t => t.type === "receita").reduce((s, t) => s + t.value, 0),
      despesa: transactionsByMonth.filtered.filter(t => t.type === "despesa").reduce((s, t) => s + t.value, 0),
    };

    return { level3TotalsByMonth, level2TotalsByMonth, level1TotalsByMonth, grandTotalsByMonth, grandTotal };
  }, [transactionsByMonth, periodColumns, level1Accounts, level2Accounts, level3Accounts]);

  // AV% bases
  const monthlyAvBase = React.useMemo(() => {
    const bases: Record<string, number> = {};
    periodColumns.forEach(period => {
      const monthTxs = transactionsByMonth.byMonth[period.key] || [];
      const receita = monthTxs.filter(t => t.type === "receita").reduce((s, t) => s + t.value, 0);
      const despesa = monthTxs.filter(t => t.type === "despesa").reduce((s, t) => s + t.value, 0);
      bases[period.key] = receita + despesa;
    });
    return bases;
  }, [transactionsByMonth, periodColumns]);

  const totalAbsolute = hierarchyData.grandTotal.receita + hierarchyData.grandTotal.despesa;
  const formatAV = (value: number) => {
    if (totalAbsolute === 0) return "-";
    const pct = (value / totalAbsolute) * 100;
    return pct === 0 ? "-" : `${pct.toFixed(1)}%`;
  };
  const formatMonthAV = (value: number, monthKey: string) => {
    const base = monthlyAvBase[monthKey] || 0;
    if (base === 0) return "-";
    const pct = (value / base) * 100;
    return pct === 0 ? "-" : `${pct.toFixed(1)}%`;
  };

  // Expand/collapse
  const toggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      newSet.has(nodeId) ? newSet.delete(nodeId) : newSet.add(nodeId);
      return newSet;
    });
  };

  const expandLevel = (level: 1 | 2 | 3) => {
    const ids = new Set<string>();
    if (level >= 2) level1Accounts.forEach(l1 => ids.add(`l1-${l1.id}`));
    if (level >= 3) level2Accounts.forEach(l2 => ids.add(`l2-${l2.id}`));
    setExpandedNodes(ids);
  };

  const expandAll = () => {
    const ids = new Set<string>();
    ids.add("saldo-inicial");
    level1Accounts.forEach(l1 => ids.add(`l1-${l1.id}`));
    level2Accounts.forEach(l2 => ids.add(`l2-${l2.id}`));
    level3Accounts.forEach(l3 => ids.add(`l3-${l3.id}`));
    setExpandedNodes(ids);
  };

  const collapseAll = () => setExpandedNodes(new Set());

  const getRowTotal = (totalsByMonth: Record<string, number>) =>
    periodColumns.reduce((sum, period) => sum + (totalsByMonth[period.key] || 0), 0);

  // Auto-resize columns
  const handleAutoResizeAll = () => {
    if (!tableContainerRef.current) return;
    const containerWidth = tableContainerRef.current.clientWidth;
    const newWidths: Record<string, number> = {};
    let totalMinWidth = 0;
    newWidths['account'] = 300; totalMinWidth += 300;
    periodColumns.forEach(period => { newWidths[period.key] = 110; totalMinWidth += 110; });
    newWidths['total'] = 130; totalMinWidth += 130;
    if (totalMinWidth < containerWidth) {
      const extra = (containerWidth - totalMinWidth) / (periodColumns.length + 2);
      Object.keys(newWidths).forEach(key => { newWidths[key] = Math.floor(newWidths[key] + extra); });
    }
    setColumnWidths(newWidths);
    setIsCentered(true);
  };

  const sortedL1 = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

  // Summary totals
  const totalOpeningTotal = getRowTotal(totalOpeningByMonth);
  const resultado = hierarchyData.grandTotal.receita - hierarchyData.grandTotal.despesa;
  const totalClosingByMonth: Record<string, number> = {};
  periodColumns.forEach(p => {
    totalClosingByMonth[p.key] = (totalOpeningByMonth[p.key] || 0) + (hierarchyData.grandTotalsByMonth[p.key] || 0);
  });
  const totalClosingTotal = getRowTotal(totalClosingByMonth);

  // ========== RENDER FUNCTIONS ==========

  const renderLevel3Rows = (l3: Level3Data) => {
    const nodeId = `l3-${l3.id}`;
    const isExpanded = expandedNodes.has(nodeId);
    const monthData = hierarchyData.level3TotalsByMonth[l3.id] || {};
    const hasTx = periodColumns.some(p => (monthData[p.key]?.transactions?.length || 0) > 0);
    if (!hasTx) return null;

    const rowTotal = periodColumns.reduce((sum, p) => sum + (monthData[p.key]?.value || 0), 0);
    const rows: React.ReactNode[] = [];

    rows.push(
      <TableRow key={`l3-${l3.id}`} className="hover:bg-gray-50 cursor-pointer transition-colors" style={{ backgroundColor: '#FFFFFF' }} onClick={() => toggleExpand(nodeId)}>
        <TableCell className="sticky left-0 z-10 border-r border-l-2 border-l-muted-foreground/15" style={{ backgroundColor: '#FFFFFF', paddingLeft: '6rem' }}>
          <div className="flex items-center gap-2">
            {hasTx ? (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
            <span className="text-[13px]">{l3.code} - {l3.name}</span>
          </div>
        </TableCell>
        {periodColumns.flatMap(period => [
          <TableCell key={period.key} className={`${isCentered ? 'text-center' : 'text-right'} text-sm border-r px-4 ${(monthData[period.key]?.value || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {(monthData[period.key]?.value || 0) !== 0 ? currency.format(monthData[period.key]?.value || 0) : "-"}
          </TableCell>,
          <TableCell key={`${period.key}-av`} className="text-center text-xs border-r px-2 text-foreground">
            {formatMonthAV(monthData[period.key]?.value || 0, period.key)}
          </TableCell>
        ])}
        <TableCell className={`text-center text-sm font-semibold border-r px-4 ${rowTotal >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {currency.format(rowTotal)}
        </TableCell>
        <TableCell className="text-center text-sm px-4 text-foreground">{formatAV(rowTotal)}</TableCell>
      </TableRow>
    );

    if (isExpanded) {
      const supplierTotals: Record<string, Record<string, number>> = {};
      periodColumns.forEach(period => {
        (monthData[period.key]?.transactions || []).forEach(tx => {
          const sid = tx.supplierId || "sem-fornecedor";
          if (!supplierTotals[sid]) supplierTotals[sid] = {};
          if (!supplierTotals[sid][period.key]) supplierTotals[sid][period.key] = 0;
          supplierTotals[sid][period.key] += tx.type === "receita" ? tx.value : -tx.value;
        });
      });

      Object.entries(supplierTotals).forEach(([supplierId, months]) => {
        const supplierTotal = periodColumns.reduce((sum, p) => sum + (months[p.key] || 0), 0);
        rows.push(
          <TableRow key={`${l3.id}-${supplierId}`} className="hover:bg-muted/15 transition-colors">
            <TableCell className="text-[13px] text-muted-foreground italic sticky left-0 z-10 border-r" style={{ backgroundColor: 'hsl(var(--background))', paddingLeft: '9rem' }}>
              {supplierId === "sem-fornecedor" ? "-" : getSupplierName(supplierId)}
            </TableCell>
            {periodColumns.flatMap(period => [
              <TableCell key={period.key} className={`${isCentered ? 'text-center' : 'text-right'} text-sm border-r px-4 ${(months[period.key] || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {months[period.key] ? currency.format(months[period.key]) : "-"}
              </TableCell>,
              <TableCell key={`${period.key}-av`} className="text-center text-xs border-r px-2 text-foreground">
                {formatMonthAV(months[period.key] || 0, period.key)}
              </TableCell>
            ])}
            <TableCell className={`text-center text-sm border-r px-4 ${supplierTotal >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {currency.format(supplierTotal)}
            </TableCell>
            <TableCell className="text-center text-sm px-4 text-foreground">{formatAV(supplierTotal)}</TableCell>
          </TableRow>
        );
      });
    }
    return rows;
  };

  const renderLevel2Rows = (l2: Level2Data) => {
    const nodeId = `l2-${l2.id}`;
    const isExpanded = expandedNodes.has(nodeId);
    const monthData = hierarchyData.level2TotalsByMonth[l2.id] || {};
    const rowTotal = getRowTotal(monthData);
    const childL3 = level3Accounts.filter(l3 => l3.level2Id === l2.id).sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
    const hasChildren = childL3.some(l3 => {
      const l3Data = hierarchyData.level3TotalsByMonth[l3.id] || {};
      return periodColumns.some(p => (l3Data[p.key]?.transactions?.length || 0) > 0);
    });
    const hasData = periodColumns.some(p => monthData[p.key] !== 0);
    if (!hasChildren && !hasData) return null;

    const rows: React.ReactNode[] = [];
    rows.push(
      <TableRow key={`l2-${l2.id}`} className="hover:opacity-90 cursor-pointer transition-colors" style={{ backgroundColor: '#FFFBEB' }} onClick={() => toggleExpand(nodeId)}>
        <TableCell className="sticky left-0 z-10 border-r border-l-[3px] border-l-muted-foreground/30 font-medium" style={{ backgroundColor: '#FFFBEB', paddingLeft: '3rem' }}>
          <div className="flex items-center gap-2">
            {hasChildren ? (isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />) : <span className="w-4" />}
            <span className="text-sm font-semibold">{l2.code} - {l2.name}</span>
          </div>
        </TableCell>
        {periodColumns.flatMap(period => [
          <TableCell key={period.key} className={`${isCentered ? 'text-center' : 'text-right'} text-sm font-semibold border-r px-4 ${(monthData[period.key] || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {monthData[period.key] !== 0 ? currency.format(monthData[period.key] || 0) : "-"}
          </TableCell>,
          <TableCell key={`${period.key}-av`} className="text-center text-xs font-semibold border-r px-2 text-foreground">
            {formatMonthAV(monthData[period.key] || 0, period.key)}
          </TableCell>
        ])}
        <TableCell className={`text-center text-sm font-bold border-r px-4 ${rowTotal >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {currency.format(rowTotal)}
        </TableCell>
        <TableCell className="text-center text-sm font-semibold px-4 text-foreground">{formatAV(rowTotal)}</TableCell>
      </TableRow>
    );

    if (isExpanded) {
      childL3.forEach(l3 => {
        const l3Rows = renderLevel3Rows(l3);
        if (l3Rows) rows.push(...(Array.isArray(l3Rows) ? l3Rows : [l3Rows]));
      });
    }
    return rows;
  };

  const renderLevel1Rows = (l1: Level1Data) => {
    const nodeId = `l1-${l1.id}`;
    const isExpanded = expandedNodes.has(nodeId);
    const monthData = hierarchyData.level1TotalsByMonth[l1.id] || {};
    const rowTotal = getRowTotal(monthData);
    const childL2 = level2Accounts.filter(l2 => l2.level1Id === l1.id).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    const hasData = periodColumns.some(p => (monthData[p.key] || 0) !== 0);
    if (!hasData) return null;

    const rows: React.ReactNode[] = [];
    rows.push(
      <TableRow key={`l1-${l1.id}`} className="hover:opacity-90 cursor-pointer border-t-2 border-border transition-colors" style={{ backgroundColor: '#FFEDD5' }} onClick={() => toggleExpand(nodeId)}>
        <TableCell className="pl-4 sticky left-0 z-10 border-r border-l-4 border-l-primary font-semibold" style={{ backgroundColor: '#FFEDD5' }}>
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="h-5 w-5 text-primary" /> : <ChevronRight className="h-5 w-5 text-primary" />}
            <span className="font-bold text-[15px]">{l1.code} - {l1.name}</span>
          </div>
        </TableCell>
        {periodColumns.flatMap(period => [
          <TableCell key={period.key} className={`${isCentered ? 'text-center' : 'text-right'} font-bold text-[15px] border-r px-4 ${(monthData[period.key] || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {monthData[period.key] !== 0 ? currency.format(monthData[period.key] || 0) : "-"}
          </TableCell>,
          <TableCell key={`${period.key}-av`} className="text-center text-xs font-bold border-r px-2 text-foreground">
            {formatMonthAV(monthData[period.key] || 0, period.key)}
          </TableCell>
        ])}
        <TableCell className={`text-center font-bold text-[15px] border-r px-4 ${rowTotal >= 0 ? "text-emerald-700" : "text-red-700"}`}>
          {currency.format(rowTotal)}
        </TableCell>
        <TableCell className="text-center font-bold text-[15px] px-4 text-foreground">{formatAV(rowTotal)}</TableCell>
      </TableRow>
    );

    if (isExpanded) {
      childL2.forEach(l2 => {
        const l2Rows = renderLevel2Rows(l2);
        if (l2Rows) rows.push(...(Array.isArray(l2Rows) ? l2Rows : [l2Rows]));
      });
    }
    return rows;
  };

  const colSpan = periodColumns.length * 2 + 3;

  const saldoInicialExpanded = expandedNodes.has("saldo-inicial");

  const reportRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main ref={reportRef} className="page-bleed py-6 space-y-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Wallet className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">DFC - Demonstração do Fluxo de Caixa</h1>
              <p className="text-sm text-muted-foreground">
                Visualização hierárquica por data de pagamento com saldos bancários
              </p>
            </div>
          </div>
          <ExportReportPdfButton
            targetRef={reportRef}
            title="DFC - Demonstração do Fluxo de Caixa"
            subtitle={`Período: ${startDate.split("-").reverse().join("/")} até ${endDate.split("-").reverse().join("/")}`}
            filenameSlug="dfc"
          />
        </div>


        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4">
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Data Inicial</Label>
                <DateInput value={startDate} onChange={(value) => value && setStartDate(value)} className="w-full sm:w-[160px]" />
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Data Final</Label>
                <DateInput value={endDate} onChange={(value) => value && setEndDate(value)} className="w-full sm:w-[160px]" />
              </div>
              <div className="space-y-2 w-full sm:w-auto">
                <Label>Centro de Custo</Label>
                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                  <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Todos os centros" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os centros</SelectItem>
                    {costCenters.map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm"><ChevronDown className="h-4 w-4 mr-2" />Expandir</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => expandLevel(1)}>Expandir Nível 1</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => expandLevel(2)}>Expandir Nível 2</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => expandLevel(3)}>Expandir Nível 3</DropdownMenuItem>
                    <DropdownMenuItem onClick={expandAll}>Expandir Tudo</DropdownMenuItem>
                    <DropdownMenuItem onClick={collapseAll}>Recolher Tudo</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="sm" onClick={handleAutoResizeAll}>
                  <Columns className="h-4 w-4 mr-2" />Ajustar Colunas
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-2" />Exportar</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => exportToExcel({ level1Accounts, level2Accounts, level3Accounts, periodColumns, hierarchyData, suppliers, startDate, endDate })}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />Excel (.xlsx)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportToPDF({ level1Accounts, level2Accounts, level3Accounts, periodColumns, hierarchyData, suppliers, startDate, endDate })}>
                      <FileText className="h-4 w-4 mr-2" />PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="text-sm text-blue-700 font-medium flex items-center gap-2"><Landmark className="h-4 w-4" />Saldo Inicial</div>
              <div className="text-2xl font-bold text-blue-700">{currency.format(totalOpeningByMonth[periodColumns[0]?.key] || 0)}</div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="py-4">
              <div className="text-sm text-emerald-700 font-medium">Total Receitas</div>
              <div className="text-2xl font-bold text-emerald-700">{currency.format(hierarchyData.grandTotal.receita)}</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="py-4">
              <div className="text-sm text-red-700 font-medium">Total Despesas</div>
              <div className="text-2xl font-bold text-red-700">{currency.format(hierarchyData.grandTotal.despesa)}</div>
            </CardContent>
          </Card>
          <Card className={`${resultado >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}>
            <CardContent className="py-4">
              <div className={`text-sm font-medium ${resultado >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>Saldo Final</div>
              <div className={`text-2xl font-bold ${resultado >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                {currency.format(totalClosingByMonth[periodColumns[periodColumns.length - 1]?.key] || 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hierarchy Table */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-lg">Estrutura Hierárquica</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div ref={tableContainerRef} className="overflow-auto max-h-[600px]" style={{ overflowX: 'auto', overflowY: 'auto' }}>
              <Table key={`table-${startDate}-${endDate}`} className="min-w-max">
                <TableHeader className="sticky top-0 z-30">
                  <TableRow className="bg-muted">
                    <TableHead className="sticky left-0 bg-muted z-20 border-r whitespace-nowrap text-center" style={{ width: columnWidths['account'] || 300, minWidth: 300 }}>
                      Conta / Fornecedor
                    </TableHead>
                    {periodColumns.flatMap(period => [
                      <TableHead key={period.key} className="text-center bg-muted border-r whitespace-nowrap px-4" style={{ width: columnWidths[period.key] || 110, minWidth: 110 }}>
                        {period.label}
                      </TableHead>,
                      <TableHead key={`${period.key}-av`} className="text-center bg-muted border-r whitespace-nowrap px-2 text-xs" style={{ width: 65, minWidth: 65 }}>
                        AV %
                      </TableHead>
                    ])}
                    <TableHead className="text-center font-bold bg-muted border-r whitespace-nowrap px-4" style={{ width: columnWidths['total'] || 130, minWidth: 130 }}>
                      Total
                    </TableHead>
                    <TableHead className="text-center font-bold bg-muted whitespace-nowrap px-4" style={{ width: 80, minWidth: 80 }}>
                      AV %
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* SALDO INICIAL row */}
                  <TableRow
                    className="hover:opacity-90 cursor-pointer border-t-2 border-border transition-colors"
                    style={{ backgroundColor: '#DBEAFE' }}
                    onClick={() => toggleExpand("saldo-inicial")}
                  >
                    <TableCell className="pl-4 sticky left-0 z-10 border-r border-l-4 border-l-blue-500 font-semibold" style={{ backgroundColor: '#DBEAFE' }}>
                      <div className="flex items-center gap-2">
                        {saldoInicialExpanded ? <ChevronDown className="h-5 w-5 text-blue-600" /> : <ChevronRight className="h-5 w-5 text-blue-600" />}
                        <Landmark className="h-4 w-4 text-blue-600" />
                        <span className="font-bold text-[15px] text-blue-800">SALDO INICIAL</span>
                      </div>
                    </TableCell>
                    {periodColumns.flatMap(period => [
                      <TableCell key={period.key} className={`text-center font-bold text-[15px] border-r px-4 ${(totalOpeningByMonth[period.key] || 0) >= 0 ? "text-blue-700" : "text-red-700"}`}>
                        {currency.format(totalOpeningByMonth[period.key] || 0)}
                      </TableCell>,
                      <TableCell key={`${period.key}-av`} className="text-center text-xs border-r px-2 text-muted-foreground">-</TableCell>
                    ])}
                    <TableCell className={`text-center font-bold text-[15px] border-r px-4 ${totalOpeningTotal >= 0 ? "text-blue-700" : "text-red-700"}`}>
                      {currency.format(totalOpeningByMonth[periodColumns[0]?.key] || 0)}
                    </TableCell>
                    <TableCell className="text-center text-sm px-4 text-muted-foreground">-</TableCell>
                  </TableRow>

                  {/* Drilldown: individual account balances */}
                  {saldoInicialExpanded && accounts.map(account => (
                    <TableRow key={`balance-${account.id}`} className="hover:bg-blue-50/50 transition-colors">
                      <TableCell className="text-[13px] text-blue-700 sticky left-0 z-10 border-r" style={{ backgroundColor: 'hsl(var(--background))', paddingLeft: '3rem' }}>
                        <div className="flex items-center gap-2">
                          <span className="w-4" />
                          <Landmark className="h-3.5 w-3.5 text-blue-500" />
                          {account.name}
                        </div>
                      </TableCell>
                      {periodColumns.flatMap(period => {
                        const bal = accountBalancesByMonth[account.id]?.[period.key] || 0;
                        return [
                          <TableCell key={period.key} className={`text-center text-sm border-r px-4 ${bal >= 0 ? "text-blue-600" : "text-red-600"}`}>
                            {currency.format(bal)}
                          </TableCell>,
                          <TableCell key={`${period.key}-av`} className="text-center text-xs border-r px-2 text-muted-foreground">-</TableCell>
                        ];
                      })}
                      <TableCell className={`text-center text-sm border-r px-4 ${(accountBalancesByMonth[account.id]?.[periodColumns[0]?.key] || 0) >= 0 ? "text-blue-600" : "text-red-600"}`}>
                        {currency.format(accountBalancesByMonth[account.id]?.[periodColumns[0]?.key] || 0)}
                      </TableCell>
                      <TableCell className="text-center text-sm px-4 text-muted-foreground">-</TableCell>
                    </TableRow>
                  ))}

                  {/* Hierarchy rows (L1 > L2 > L3 > Suppliers) */}
                  {sortedL1.length > 0 ? (
                    sortedL1.flatMap(l1 => {
                      const rows = renderLevel1Rows(l1);
                      return rows ? (Array.isArray(rows) ? rows : [rows]) : [];
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">
                        Nenhum dado disponível. Configure o Plano de Contas para começar.
                      </TableCell>
                    </TableRow>
                  )}

                  {/* RESULTADO row */}
                  {transactionsByMonth.filtered.length > 0 && (
                    <TableRow className="border-t-2 border-primary font-bold" style={{ backgroundColor: '#FFEDD5' }}>
                      <TableCell className="sticky left-0 z-10 border-r" style={{ backgroundColor: '#FFEDD5' }}>
                        <span className="font-bold text-lg">RESULTADO</span>
                      </TableCell>
                      {periodColumns.flatMap(period => {
                        const value = hierarchyData.grandTotalsByMonth[period.key] || 0;
                        return [
                          <TableCell key={period.key} className={`text-center font-bold border-r px-4 ${value >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {value !== 0 ? currency.format(value) : "-"}
                          </TableCell>,
                          <TableCell key={`${period.key}-av`} className="text-center text-xs font-bold border-r px-2">100%</TableCell>
                        ];
                      })}
                      <TableCell className={`text-center font-bold text-lg border-r px-4 ${resultado >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {currency.format(resultado)}
                      </TableCell>
                      <TableCell className="text-center font-bold text-lg px-4">100%</TableCell>
                    </TableRow>
                  )}

                  {/* SALDO FINAL row */}
                  <TableRow className="border-t-2 border-blue-400 font-bold" style={{ backgroundColor: '#DBEAFE' }}>
                    <TableCell className="pl-4 sticky left-0 z-10 border-r border-l-4 border-l-blue-500" style={{ backgroundColor: '#DBEAFE' }}>
                      <div className="flex items-center gap-2">
                        <Landmark className="h-5 w-5 text-blue-600" />
                        <span className="font-bold text-lg text-blue-800">SALDO FINAL</span>
                      </div>
                    </TableCell>
                    {periodColumns.flatMap(period => {
                      const value = totalClosingByMonth[period.key] || 0;
                      return [
                        <TableCell key={period.key} className={`text-center font-bold text-[15px] border-r px-4 ${value >= 0 ? "text-blue-700" : "text-red-700"}`}>
                          {currency.format(value)}
                        </TableCell>,
                        <TableCell key={`${period.key}-av`} className="text-center text-xs border-r px-2 text-muted-foreground">-</TableCell>
                      ];
                    })}
                    <TableCell className={`text-center font-bold text-lg border-r px-4 ${totalClosingTotal >= 0 ? "text-blue-700" : "text-red-700"}`}>
                      {currency.format(totalClosingByMonth[periodColumns[periodColumns.length - 1]?.key] || 0)}
                    </TableCell>
                    <TableCell className="text-center text-sm px-4 text-muted-foreground">-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
